import { MccsRoles } from '@/@types/generated'
import { hasRole } from '@/components/Atoms/Permissions'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useAuth } from '@/hooks/useAuth'
import { useError } from '@/hooks/useError'
import { EyeIcon } from 'lucide-react'
import { PropsWithChildren, useState } from 'react'

interface RevealButtonProps {
  // Method to call to retrieve the secret value
  getSecret: () => Promise<string>

  // Can reveal the secret value
  // Admins, Cred. Admins, and Chief Inv. can always reveal
  // If specified, and true, allow revealing
  canReveal?: boolean
}

export function RevealButton({
  getSecret,
  canReveal: parentCanReveal,
  children,
}: PropsWithChildren<RevealButtonProps>) {
  // Hooks
  const { showError } = useError()
  const { user } = useAuth()

  // State
  const [isLoading, setIsLoading] = useState(false)
  const [secret, setSecret] = useState<string | null>(null)

  // Retrieve the secret value and replace it
  const onClick = async () => {
    try {
      setIsLoading(true)

      const secret = await getSecret()
      setSecret(secret)
    } catch (e: unknown) {
      showError(e, 'Failed to retrieve secret')
    } finally {
      setIsLoading(false)
    }
  }

  // Can reveal
  const canReveal =
    parentCanReveal ||
    hasRole([MccsRoles.CREDENTIALING_ADMIN, MccsRoles.CHIEF_INVESTIGATOR], user)

  // No permission
  if (!canReveal) return children

  return (
    <>
      {secret ? (
        secret
      ) : (
        <div className="flex items-center gap-2">
          {children}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="xs"
                aria-label="Reveal"
                onClick={onClick}
                disabled={isLoading}
              >
                {isLoading ? (
                  <Spinner />
                ) : (
                  <EyeIcon className="stroke-primary" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">Reveal</TooltipContent>
          </Tooltip>
        </div>
      )}
    </>
  )
}
